import type { BlinkEvent } from "../core/types.ts";
import { type Mechanic, type Readout, relief } from "./mechanic.ts";

/**
 * Mechanic B — traverse on blackout (design doc §4B).
 *
 * The 100–400ms of blindness during a blink stops being a bug and becomes the
 * game: the world only moves while you cannot see it. Blinking is how you
 * travel. It is the most original direction available, because no other input
 * modality has this property.
 *
 * ## Why this advances per blink and not while the eyes are closed
 *
 * The obvious build — advance while `closure >= closeThreshold` — inverts the
 * thesis in design doc §1. It makes *holding your eyes shut* the optimal
 * strategy, which is the precise failure the document opens by warning about,
 * and it would train the worst possible habit while claiming to treat it.
 *
 * Crediting per emitted blink fixes it for free, because the detector's own
 * duration gate is already an anti-exploit: `minDurationMs: 40` and
 * `maxDurationMs: 600` mean a deliberate five-second closure emits **nothing**
 * and therefore earns nothing. A natural blink rate is the fastest possible
 * traversal. Suppression stalls the world — nothing happens, which is legible
 * and is the whole penalty.
 *
 * The other reason is simpler: motion during a closure is invisible by
 * construction. The player's eyes are shut. Only the delta is observable, so
 * animating through the closure buys nothing and costs a second clock.
 */

/**
 * Cumulative credited closure for one crossing.
 *
 * Set from the measured rate, not from a guess about how many blinks feel like
 * enough. Real sessions emit 23.5 blinks/min at a median 267ms and amplitude
 * 0.62, so the first version's 2200ms goal took **34 seconds** of watching a
 * dot creep across a screen — long past the point where the connection between
 * blinking and travelling stops being legible.
 *
 * 1300ms puts a crossing at roughly six blinks, about fifteen seconds, so a
 * 90-second session contains half a dozen of them and the causal link is
 * obvious within the first two.
 */
const GOAL_MS = 1300;

/**
 * Most duration a single blink can bank.
 *
 * The anti-exploit: paying linearly for longer closures makes a slow deliberate
 * one the winning strategy, which is the habit the product exists to undo.
 *
 * Measured blink durations are p50 267ms, p90 465ms, p99 597ms. The first
 * version capped at 400ms, which is p85 — so it was clipping one real blink in
 * seven. 450ms sits at the measured p90: it still bites well before the
 * detector's own 600ms ceiling, without taxing ordinary blinking.
 */
const CREDIT_CAP_MS = 450;

/** Fraction of the track the marker itself occupies. */
const MARKER_SPAN = 8;

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

export class Blackout implements Mechanic {
  readonly id = "blackout" as const;
  readonly hint = "blink to cross · the world only moves when you cannot see it";

  #progress = 0;
  #laps = 0;
  #advances = 0;
  /** Rendered position, eased toward #progress so a crossing reads as motion. */
  #shown = 0;

  constructor(private readonly marker: HTMLElement) {}

  /**
   * Credit is continuous in both duration and amplitude, and reads neither
   * `kind` nor any threshold. A half blink moves you half as far — design doc
   * §5 with no classifier in the path.
   */
  onBlink(event: BlinkEvent): void {
    const duration = Math.min(event.duration, CREDIT_CAP_MS);
    const credit = (duration / GOAL_MS) * relief(event.amplitude);
    if (credit <= 0) return;

    this.#advances++;
    this.#progress += credit;
    while (this.#progress >= 1) {
      this.#progress -= 1;
      this.#shown -= 1;
      this.#laps++;
    }
  }

  update(): void {
    // Eased rather than snapped: the jump happens while the eyes are shut, so
    // easing is what makes it legible on reopening instead of a teleport that
    // reads as a glitch. Frame-rate coupled like FatigueBlur's, and for the
    // same reason — it is a feel constant, not a physical one.
    this.#shown += (this.#progress - this.#shown) * 0.18;
    if (this.#shown < 0) this.#shown = 0;

    // Percentage of the track, so the mechanic never reads layout.
    const left = clamp01(this.#shown) * (100 - MARKER_SPAN);
    this.marker.style.left = `${left.toFixed(2)}%`;
  }

  readout(): Readout {
    return {
      value: `${Math.round(clamp01(this.#progress) * 100)}%`,
      label: this.#laps > 0 ? `crossed ${this.#laps}` : "crossed",
    };
  }

  get progress(): number {
    return this.#progress;
  }

  get laps(): number {
    return this.#laps;
  }

  /** Blinks that moved the marker. Zero with a face tracked means classification. */
  get advances(): number {
    return this.#advances;
  }

  reset(): void {
    this.#progress = 0;
    this.#laps = 0;
    this.#advances = 0;
    this.#shown = 0;
    this.marker.style.left = "";
  }
}

export { CREDIT_CAP_MS, GOAL_MS };
